const MASTER_CACHE_NAME = "duallo-model-cache";

class ModelCache {
    constructor(cacheName = MASTER_CACHE_NAME) {
        this.cacheName = cacheName;
    }

    async fetchModelFromCache(url) {
        const cache = await this.getCache();
        const splitUrl = this.splitUrl(url);
        const cachedResponse = await cache.match(splitUrl);

        if (cachedResponse) {
            console.log(`Loaded model from cache: ${splitUrl}`);
            return await cachedResponse.arrayBuffer();
        }

        return new ArrayBuffer(0);
    }

    async saveModelToCache(url, response) {
        const cache = await this.getCache();
        const splitUrl = this.splitUrl(url);
        await cache.put(splitUrl, response.clone());
    }

    splitUrl(url) {
        return url.split("?")[0];
    }

    async getCache() {
        return await caches.open(this.cacheName);
    }

    async getAllRequestsAsync() {
        const cache = await this.getCache();
        return await cache.keys();
    }

    async deleteModelRecordAsync(model){
        const cache = await this.getCache();
        return await cache.delete(model.url);
    }

    // Size (bytes) of a cached entry, read from the Content-Length header saved alongside the body.
    // Returns 0 when the entry is missing or was cached before sizes were stored.
    async getContentLengthAsync(request){
        const cache = await this.getCache();
        const cachedResponse = await cache.match(request);
        if (!cachedResponse) return 0;
        return Number(cachedResponse.headers.get("Content-Length")) || 0;
    }

    // Remove every cached model for this device. Returns the number of entries deleted.
    async clearAllAsync(){
        const cache = await this.getCache();
        const keys = await cache.keys();
        await Promise.all(keys.map((key) => cache.delete(key)));
        return keys.length;
    }

    // When a BAKED model is cached, the original full-fat scan PLY for the same scan is no longer used
    // by the viewer, so drop it to avoid keeping both copies on the device. Safe because the raw PLY is
    // the server-side source of truth (never deleted) and re-downloads on demand. No-op for raw URLs,
    // so this can be called for every load.
    //   baked: {root}/{assetId}/modelversions/{versionId}/{scanId}.ply
    //   raw:   {root}/{assetId}/scans/{scanId}/model/{scanId}.ply
    async evictSupersededRawAsync(bakedUrl){
        const path = this.splitUrl(bakedUrl);
        const marker = "/modelversions/";
        const markerIndex = path.indexOf(marker);
        if (markerIndex === -1) return false; // not a baked path → nothing is superseded

        const assetPrefix = path.slice(0, markerIndex); // {root}/{assetId}
        const scanId = path.split("/").pop().replace(/\.ply$/i, "");
        const rawKey = `${assetPrefix}/scans/${scanId}/model/${scanId}.ply`;

        const cache = await this.getCache();
        return await cache.delete(rawKey);
    }

    getCacheName(){
        return this.cacheName;
    }
}

const modelCache = new ModelCache();

export { ModelCache, modelCache, MASTER_CACHE_NAME };
export default modelCache;